import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    AdornedInput, Adornment, FormField, FormHelp, Label, Paragraph, Flex, Input,
} from '@zillow/constellation';
import {
    Col, Row, Center,
} from '@zillow/zrs-comet';

import appLogger from 'app/shared/loggers/appLogger';

import RentGuaranteeActions from 'app/shared/actions/RentGuaranteeActions';
import PostingActions from 'app/shared/actions/PostingActions';

import WizardBodyTemplate from 'app/shared/components/WizardBodyTemplate';
import WizardActionFooter from 'app/shared/components/WizardActionFooter';

import TwoWaysToPay from './TwoWaysToPay';
import RadioButton from '../components/RadioButton';

import { StyledHR } from './StyledComponents';

const WAIVE_SECURITY_DEPOSIT = 'waiveSecurityDeposit';
const REQUIRE_SECURITY_DEPOSIT = 'requireSecurityDeposit';

const propTypes = {
    onGoToPrevStep: PropTypes.func.isRequired,
    onGoToNextStep: PropTypes.func.isRequired,
    propertyId: PropTypes.string.isRequired,
    progressStepper: PropTypes.node.isRequired,
};
const defaultProps = {};

const VerifySecurityDeposit = ({
    onGoToNextStep,
    onGoToPrevStep,
    propertyId,
    progressStepper,
}) => {
    const dispatch = useDispatch();
    const securityDepositSelector = useSelector(state => state.rentGuarantee.securityDeposit);
    const monthlyRentSelector = useSelector(state => state.rentGuarantee.monthlyRent);
    const [securityDepositChoice, setSecurityDepositChoice] = useState(securityDepositSelector);
    const [securityDeposit, setSecurityDeposit] = useState(securityDepositSelector);
    const [monthlyRent, setMonthlyRent] = useState(monthlyRentSelector);
    const [floorPlanId, setFloorPlanId] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [isNextDisabled, setIsNextDisabled] = useState(true);

    useEffect(() => {
        if (!securityDeposit || !monthlyRent) {
            dispatch(PostingActions.property.getDetails(propertyId))
                .then((response) => {
                    const { floorplans } = response;

                    if (floorplans.length) {
                        const { securityDeposit, price, floorPlanId } = floorplans[0];
                        setSecurityDeposit(securityDeposit);
                        setMonthlyRent(price);
                        setFloorPlanId(floorPlanId);

                        RentGuaranteeActions.setMonthlyRent(price);
                    }
                })
                .catch((errorRMWModel) => {
                    appLogger.logError({
                        message: errorRMWModel.message,
                        context: 'RentGuaranteePage.fetchPropertyDetails',
                    });
                });
        }
    }, []);

    useEffect(() => {
        if (securityDepositChoice === WAIVE_SECURITY_DEPOSIT && paymentMethod) {
            setIsNextDisabled(false);
        } else if (securityDepositChoice === REQUIRE_SECURITY_DEPOSIT && securityDeposit !== null) {
            setIsNextDisabled(false);
        } else {
            setIsNextDisabled(true);
        }
    }, [securityDepositChoice, paymentMethod, securityDeposit]);

    const handleWaiveSecurityDepositClick = () => {
        setSecurityDepositChoice(WAIVE_SECURITY_DEPOSIT);

        setTimeout(() => {
            const element = document.getElementById('two-ways-to-pay');
            if (element != null && 'scrollIntoView' in element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        });
    };

    const handleRequireSecurityDepositClick = () => {
        setSecurityDepositChoice(REQUIRE_SECURITY_DEPOSIT);

        setTimeout(() => {
            const element = document.getElementById('security-deposit');
            if (element != null && 'scrollIntoView' in element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        });
    };

    const onSecurityDepositChange = (event) => {
        const value = Number(event.target.value);
        setSecurityDeposit(value);
    };

    const handleNextClick = () => {
        if (securityDepositChoice === REQUIRE_SECURITY_DEPOSIT && securityDeposit) {
            dispatch(RentGuaranteeActions.setSecurityDeposit(securityDeposit));

            const data = {
                fieldName: 'securityDeposit',
                value: securityDeposit,
                floorPlanId,
            };
            dispatch(PostingActions.floorplan.edit(data));
        }
        onGoToNextStep();
    };

    const renderSubtitle = () => (
        <>
            <Paragraph fontType="bodyHeading">
                Rent Guarantee protects your investment, so you can:
            </Paragraph>
            <Paragraph>- Skip the hassle of receiving, holding, and refunding a deposit.</Paragraph>
            <Paragraph marginBottom="lg">
                - Offer an attractive, modern benefit that helps your property stand out to renters who value
                 financial flexibility.
            </Paragraph>
            <Paragraph>
                If you choose to require a security deposit, this amount will be deducted from your Rent
                Guarantee coverage if you submit a claim
            </Paragraph>
        </>
    );

    return (
        <WizardBodyTemplate
            title="Require security deposit?"
            subtitle={renderSubtitle()}
            progressStepper={progressStepper}
            footer={(
                <WizardActionFooter
                    onBackClick={onGoToPrevStep}
                    onNextClick={handleNextClick}
                    isNextDisabled={isNextDisabled}
                />
            )}
        >
            <Row marginTop="xl" marginBottom="md">
                <Col xs={6}>
                    <RadioButton
                        onClick={handleWaiveSecurityDepositClick}
                        name="security-deposit-choice"
                        id="waive-security-deposit"
                        label="Waive security deposit"
                        checked={securityDepositChoice === WAIVE_SECURITY_DEPOSIT}
                    />
                </Col>
                <Col xs={6}>
                    <RadioButton
                        onClick={handleRequireSecurityDepositClick}
                        name="security-deposit-choice"
                        id="require-security-deposit"
                        label="Require security deposit"
                        checked={securityDepositChoice === REQUIRE_SECURITY_DEPOSIT}
                    />
                </Col>
            </Row>
            {securityDepositChoice === WAIVE_SECURITY_DEPOSIT && (
                <TwoWaysToPay
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    monthlyRent={monthlyRent}
                />
            )}
            {securityDepositChoice === REQUIRE_SECURITY_DEPOSIT && (
                <div id="security-deposit">
                    <Flex display="flex" alignItems="center" flexDirection="column">
                        <StyledHR color="yellow300" />
                        <Center>
                            <Paragraph fontType="h4">How much security deposit?</Paragraph>
                            <Paragraph marginBottom="lg">
                                Requiring a lower security deposit could make your listing more attractive to renters.
                                Since Rent Guarantee provides you with up to $5,000 coverage, you may not need to
                                collect the full deposit you’d require without coverage.
                            </Paragraph>
                            <Paragraph fontType="bodyHeading">
                                Consider lowering your security deposit?
                            </Paragraph>
                        </Center>
                    </Flex>
                    <FormField
                        label={<Label>Security deposit</Label>}
                        control={(
                            <AdornedInput
                                leftAdornment={<Adornment>$</Adornment>}
                                input={(
                                    <Input
                                        aria-label="Security deposit"
                                        value={securityDeposit}
                                        onChange={onSecurityDepositChange}
                                    />
                                )}
                            />
                        )}
                        description={<FormHelp>Amount should generally not exceed 150% of the base rent</FormHelp>}
                    />
                </div>
            )}
        </WizardBodyTemplate>
    );
};

VerifySecurityDeposit.propTypes = propTypes;
VerifySecurityDeposit.defaultProps = defaultProps;

export default VerifySecurityDeposit;
